/** «Рассылки» → «По шаблонам» — ровно то, что отдаёт `GET /api/mailing/templates`. */
import { apiFetch, query } from "@/lib/api";

/**
 * Шаг получателя после отправки; `failed` — Wazzup вернул ошибку доставки (у MAX и Telegram с наших
 * номеров — нет мессенджера или сбой отправки), `broken` — из них сбой отправки, `replied` — написал в
 * WhatsApp до следующего шаблона, `booked` — сам выбрал время в кабинете, `recorded` — итог по
 * сделке: после отправки сделка номера вошла в «Ждем клиента», `reserved` — договор сделки номера
 * вошёл в «Бронь».
 */
export type TemplateStep =
  | "failed"
  | "broken"
  | "delivered"
  | "read"
  | "replied"
  | "opened"
  | "booked"
  | "click_max"
  | "click_telegram"
  | "click_whatsapp"
  | "bot_max"
  | "bot_telegram"
  | "recorded"
  | "reserved";

/**
 * Счёт шаблона: получатель считается в каждом шаге один раз; `replied_recorded` и
 * `replied_reserved` — из ответивших записаны и забронировали. `sent_wa` — из отправленных ушло
 * шаблоном WhatsApp (остальное — бесплатным каналом: бот, площадка): доставка, прочтение и ответ
 * есть только у них.
 */
export type TemplateCounts = {
  sent: number;
  sent_wa: number;
  replied_recorded: number;
  replied_reserved: number;
} & Record<TemplateStep, number>;

export type TemplateRow = TemplateCounts & {
  key: string;
  title: string;
  /** Куда ведут кнопки: `zapis` — страница записи, `deal` — боты с хэшем брони, `null` — ссылок нет. */
  link: "zapis" | "deal" | null;
  /** Стадия, к которой привязана рассылка, — ключ из `groups`. */
  group: string;
  /** Своя рассылка (`mailing_task`); `false` — шаблон WhatsApp, который шлём не мы. */
  own: boolean;
  /** Та же воронка по каналам, которыми ушло (больше отправленных — первыми): `whatsapp` — шаблон,
   * `whatsapp_window` — текст в окне 24 ч, боты и площадки, `free` — чат не нашли. */
  channels: (TemplateCounts & { channel: string })[];
};

export type TemplateItem = {
  /** Когда отправлено, MSK: `2026-10-02 16:34`. */
  at: string;
  kind: string;
  phone: string;
  /** Лид и сделка рассылки; у чужого шаблона — последние по номеру. */
  lead_id: number | null;
  /** Статус лида сейчас, названием: «Не обработан»; пусто — лида нет в копии Bitrix. */
  lead_status: string;
  deal_id: number | null;
  /** Стадия сделки сейчас, названием: «Ждем клиента». */
  deal_stage: string;
  /** Статус сообщения в Wazzup: sent, delivered, read, error; у бесплатного канала пусто. */
  status: string;
  /** Чем ушло: `whatsapp` — шаблон WABA; иначе канал чата (telegram, max, vk, avito, instagram), `free` — чат не нашли. */
  channel: string;
  /** Когда клиент ответил в WhatsApp (первое входящее до следующего шаблона), MSK; `null` — не ответил. */
  replied_at: string | null;
  /** Чат получателя в «Чатах»: WhatsApp номера, у бесплатного канала — чат, куда ушло; `null` — чата нет. */
  chat_id: number | null;
  /** Чат сейчас «Без ответа»: последнее сообщение — клиента, отметки «Ответили» нет. */
  waiting: boolean;
  /** Страница клиента из кнопки шаблона. */
  url: string | null;
  steps: TemplateStep[];
};

export type MailingTemplates = {
  from: string;
  to: string;
  /** Группы строк по порядку движения лида: лид → в работе → ждем клиента → после примерки → забронировано. */
  groups: { key: string; title: string }[];
  rows: TemplateRow[];
  total: TemplateCounts;
  /** Отправок за период; `items` — не больше 1000 свежих. */
  count: number;
  items: TemplateItem[];
};

export function loadMailingTemplates(from: string, to: string): Promise<MailingTemplates> {
  return apiFetch<MailingTemplates>(`/api/mailing/templates${query({ from, to })}`);
}

/** Очередь своей рассылки сейчас и последний запуск её фона. */
export type TemplateQueue = {
  /** Задач в очереди (`mailing_task` в `wait`). */
  wait: number;
  /** Ушло бы прямо сейчас — предпросмотр правилами отправки; `null` — CRM недоступна, не разобрали. */
  ready: number | null;
  /** Ждут: окно салона, тишина по номеру. */
  hold: number;
  /** Снимутся при отправке: стадия ушла, запись старше срока. */
  cancel: number;
  /** Причина → сколько задач. */
  notes: Record<string, number>;
  /** Повод самой старой задачи очереди, MSK. */
  oldest: string | null;
  /** Последняя отправка этой рассылки, MSK. */
  last_sent: string | null;
  /** Задание диспетчера: mailing_lead, mailing_address, mailing_contract, mailing_fitting, mailing_remind. */
  task: string;
  /** Последний запуск задания, MSK: `2026-10-03 07:04`; `null` — не запускалось. */
  run_at: string | null;
  run_ago: number | null;
  run_error: string;
  /** Флаг отправки включён: фон пишет клиентам. */
  send_on: boolean;
  /** Почему очередь может не уходить; пусто — порядок. */
  alarm: string;
};

export type MailingQueues = { at: string; kinds: Record<string, TemplateQueue> };

export function loadMailingQueues(): Promise<MailingQueues> {
  return apiFetch<MailingQueues>("/api/mailing/templates/queue");
}
